import * as React from "react";
import { RecurringAvailability as RecurringAvailabilityType, RecurringAvailabilityProps, RecurringAvailabilityFormData } from "../types";
import TimeInput from "./TimeInput";
import { Form } from "react-bootstrap";

const RecurringAvailability: React.FC<RecurringAvailabilityProps> = ({
  profileId,
  alwaysExpanded = false,
  collapsed = false,
  onComplete,
  onEdit
}) => {
  const [data, setData] = React.useState<RecurringAvailabilityType | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = React.useState<RecurringAvailabilityFormData | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  const daysOfWeek = [
    { key: "monday", label: "Mon" },
    { key: "tuesday", label: "Tue" },
    { key: "wednesday", label: "Wed" },
    { key: "thursday", label: "Thu" },
    { key: "friday", label: "Fri" },
    { key: "saturday", label: "Sat" },
    { key: "sunday", label: "Sun" },
  ];

  const initializeFormData = (data: RecurringAvailabilityType) => {
    setFormData({
      sunday_availability: data.sunday.available,
      sunday_start: data.sunday.start,
      sunday_end: data.sunday.end,
      monday_availability: data.monday.available,
      monday_start: data.monday.start,
      monday_end: data.monday.end,
      tuesday_availability: data.tuesday.available,
      tuesday_start: data.tuesday.start,
      tuesday_end: data.tuesday.end,
      wednesday_availability: data.wednesday.available,
      wednesday_start: data.wednesday.start,
      wednesday_end: data.wednesday.end,
      thursday_availability: data.thursday.available,
      thursday_start: data.thursday.start,
      thursday_end: data.thursday.end,
      friday_availability: data.friday.available,
      friday_start: data.friday.start,
      friday_end: data.friday.end,
      saturday_availability: data.saturday.available,
      saturday_start: data.saturday.start,
      saturday_end: data.saturday.end,
    });
  };

  // Fetch current recurring availability
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`/api/v1/profiles/${profileId}/recurring_availability`);
        if (response.ok) {
          const jsonData = await response.json();
          setData(jsonData);
          initializeFormData(jsonData);

          // If alwaysExpanded mode, automatically enter edit mode
          if (alwaysExpanded) {
            setIsEditing(true);
          }
        } else {
          setError("Failed to load recurring availability");
        }
      } catch (err) {
        setError("An error occurred while loading data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [profileId, alwaysExpanded]);

  const handleEditClick = () => {
    setIsEditing(true);
    setFormError(null);
    if (onEdit) onEdit();
  };

  const handleCancel = () => {
    if (alwaysExpanded) {
      // In always-expanded mode, cancel just resets the form
      if (data) {
        initializeFormData(data);
      }
      setFormError(null);
    } else {
      // In normal mode, exit edit mode
      setIsEditing(false);
      setFormError(null);
      if (data) {
        initializeFormData(data);
      }
    }
  };

  const handleCheckboxChange = (day: string, checked: boolean) => {
    const startKey = `${day}_start` as keyof RecurringAvailabilityFormData;
    const endKey = `${day}_end` as keyof RecurringAvailabilityFormData;
    const currentStart = formData![startKey] as string;
    const currentEnd = formData![endKey] as string;

    // When marking a day as available, set default times if not already set
    const updates: Partial<RecurringAvailabilityFormData> = {
      [`${day}_availability`]: checked,
    };

    if (checked) {
      if (!currentStart) {
        updates[startKey] = "9:00 AM";
      }
      if (!currentEnd) {
        updates[endKey] = "5:00 PM";
      }
    }

    setFormData({
      ...formData!,
      ...updates,
    });
  };

  const handleTimeChange = (day: string, field: "start" | "end", value: string) => {
    setFormData({
      ...formData!,
      [`${day}_${field}`]: value,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);

    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/v1/profiles/${profileId}/recurring_availability`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          recurring_availability: formData,
        }),
      });

      if (response.ok) {
        const { data: updatedData } = await response.json();
        setData(updatedData);
        initializeFormData(updatedData);

        // In normal mode, exit edit mode after save
        if (!alwaysExpanded) {
          setIsEditing(false);
        }

        // Call onComplete callback if provided
        if (onComplete) {
          onComplete();
        }
      } else {
        const errorData = await response.json();
        setFormError(errorData.errors?.join(", ") || "Failed to save recurring availability");
      }
    } catch (err) {
      setFormError("An error occurred while saving");
    } finally {
      setSaving(false);
    }
  };

  const hasAnyAvailability = data && daysOfWeek.some((day) => data[day.key as keyof RecurringAvailabilityType].available);

  // Generate summary text for collapsed view
  const getSummaryText = (): string => {
    if (!data) return "No schedule set";

    const availableDays = daysOfWeek.filter(
      (day) => data[day.key as keyof RecurringAvailabilityType].available
    );

    if (availableDays.length === 0) return "No availability set";

    // Check if all available days have the same times
    const firstDay = data[availableDays[0].key as keyof RecurringAvailabilityType];
    const allSameTimes = availableDays.every((day) => {
      const dayData = data[day.key as keyof RecurringAvailabilityType];
      return dayData.start === firstDay.start && dayData.end === firstDay.end;
    });

    if (allSameTimes && availableDays.length > 0) {
      const dayNames = availableDays.map((d) => d.label).join(", ");
      return `${dayNames}: ${firstDay.start} - ${firstDay.end}`;
    }

    return `${availableDays.length} days configured`;
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  // Collapsed summary view (for step 2)
  if (collapsed && data) {
    return (
      <div
        className="content-card mb-3"
        style={{
          background: "white",
          border: "1px solid #e8ecef",
          borderRadius: "0.75rem",
          padding: "1rem 1.25rem",
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="fas fa-calendar-check" style={{ color: "#0f4b89" }}></i>
              <strong style={{ color: "#034ba6" }}>Weekly Schedule</strong>
            </div>
            <div className="text-muted small">{getSummaryText()}</div>
          </div>
          <button
            className="btn-modern btn-modern--ghost btn-modern--sm"
            onClick={handleEditClick}
            style={{
              background: "transparent",
              border: "2px solid #e8ecef",
              color: "#0f4b89",
              padding: "0.5rem 1rem",
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <i className="fa fa-edit"></i> Edit
          </button>
        </div>
      </div>
    );
  }

  // Form view (always-expanded or editing mode)
  if (alwaysExpanded || isEditing) {
    return (
      <div className="recurring-availability-container">
        {formData && (
          <div
            className="form-section-card"
            style={{
              background: "white",
              padding: "1.5rem",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
            }}
          >
            {formError && (
              <div className="alert alert-danger py-2 mb-3" role="alert">
                {formError}
              </div>
            )}

            <div className="recurring-availability-form">
              {daysOfWeek.map((day, index) => {
                const isAvailable = formData[`${day.key}_availability` as keyof RecurringAvailabilityFormData];
                const startValue = formData[`${day.key}_start` as keyof RecurringAvailabilityFormData] as string;
                const endValue = formData[`${day.key}_end` as keyof RecurringAvailabilityFormData] as string;

                return (
                  <div
                    key={day.key}
                    className="d-flex align-items-center py-2"
                    style={{
                      borderBottom: index < daysOfWeek.length - 1 ? "1px solid #f0f0f0" : "none",
                      backgroundColor: isAvailable ? "rgba(113, 173, 8, 0.05)" : "transparent",
                      marginLeft: "-0.5rem",
                      marginRight: "-0.5rem",
                      paddingLeft: "0.5rem",
                      paddingRight: "0.5rem",
                      borderRadius: "8px",
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <div style={{ width: "50px" }} className="fw-semibold" >
                      {day.label}
                    </div>
                    <div style={{ width: "110px" }}>
                      <Form.Check
                        type="checkbox"
                        id={`${day.key}-checkbox-form`}
                        label={<span className="small text-muted">Available</span>}
                        checked={!!isAvailable}
                        onChange={(e) => handleCheckboxChange(day.key, e.target.checked)}
                        className="m-0"
                      />
                    </div>
                    <div className="flex-grow-1 d-flex align-items-center gap-2">
                      <div style={{ flex: 1, maxWidth: "140px" }}>
                        <TimeInput
                          value={startValue}
                          onChange={(value) => handleTimeChange(day.key, "start", value)}
                          placeholder="9:00 AM"
                          disabled={!isAvailable}
                          compact
                        />
                      </div>
                      <span className="text-muted small">to</span>
                      <div style={{ flex: 1, maxWidth: "140px" }}>
                        <TimeInput
                          value={endValue}
                          onChange={(value) => handleTimeChange(day.key, "end", value)}
                          placeholder="5:00 PM"
                          disabled={!isAvailable}
                          compact
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="d-flex gap-2 justify-content-end mt-4">
              {!alwaysExpanded && (
                <button
                  className="btn-modern btn-modern--ghost"
                  onClick={handleCancel}
                  disabled={saving}
                  style={{
                    background: "transparent",
                    border: "2px solid #e8ecef",
                    color: "#0f4b89",
                    padding: "0.625rem 1.25rem",
                    borderRadius: "0.5rem",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              )}
              <button
                className="btn-modern btn-modern--primary"
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: "linear-gradient(135deg, #0f4b89, #1a5a9e)",
                  color: "white",
                  border: "none",
                  padding: "0.625rem 1.25rem",
                  borderRadius: "0.5rem",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(15, 75, 137, 0.3)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save"></i>
                    {onComplete ? "Save & Continue" : "Save Changes"}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Normal view mode (not editing, not always-expanded, not collapsed)
  return (
    <div className="recurring-availability-container">
      {hasAnyAvailability ? (
        <div
          className="content-card"
          style={{
            background: "white",
            border: "1px solid #e8ecef",
            borderRadius: "0.75rem",
            padding: "1.25rem",
          }}
        >
          <div className="recurring-availability-list">
            {daysOfWeek.map((day) => {
              const dayData = data![day.key as keyof RecurringAvailabilityType];
              if (!dayData.available) return null;

              return (
                <div
                  key={day.key}
                  className="d-flex align-items-center py-2"
                  style={{
                    borderBottom: "1px solid #f5f5f5",
                  }}
                >
                  <div style={{ width: "60px" }}>
                    <span
                      className="badge-modern badge-modern--primary"
                      style={{
                        background: "linear-gradient(135deg, #0f4b89, #1a5a9e)",
                        color: "white",
                        padding: "0.25rem 0.625rem",
                        borderRadius: "50px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                    >
                      {day.label}
                    </span>
                  </div>
                  <div className="flex-grow-1">
                    <span
                      style={{
                        color: "#71ad08",
                        fontWeight: 500,
                        fontSize: "0.9rem",
                      }}
                    >
                      <i className="fas fa-clock me-1"></i>
                      {dayData.start} - {dayData.end}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-3">
            <button
              onClick={handleEditClick}
              className="btn-modern btn-modern--ghost"
              style={{
                background: "transparent",
                border: "2px solid #e8ecef",
                color: "#0f4b89",
                padding: "0.5rem 1rem",
                borderRadius: "0.5rem",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <i className="fa fa-edit"></i>
              Edit Recurring Hours
            </button>
          </div>
        </div>
      ) : (
        <div className="empty-state text-center p-4">
          <div className="empty-state__icon mb-3">
            <i className="fa fa-calendar-week" style={{ fontSize: "2.5rem", color: "#6c757d" }}></i>
          </div>
          <h5 className="empty-state__title" style={{ color: "#333", marginBottom: "0.5rem", fontWeight: 600 }}>
            No Recurring Availability Set
          </h5>
          <p className="empty-state__description" style={{ color: "#6c757d", marginBottom: "1rem", fontSize: "0.9rem" }}>
            Set your weekly recurring availability to let practices know when you are generally available.
          </p>
          <button
            onClick={handleEditClick}
            className="btn-modern btn-modern--primary"
            style={{
              background: "linear-gradient(135deg, #0f4b89, #1a5a9e)",
              color: "white",
              border: "none",
              padding: "0.625rem 1.25rem",
              borderRadius: "0.5rem",
              fontSize: "0.95rem",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(15, 75, 137, 0.3)",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <i className="fa fa-calendar-plus"></i>
            Set Recurring Hours
          </button>
        </div>
      )}
    </div>
  );
};

export default RecurringAvailability;
